import type {
  Risk,
  RiskProbability,
  RiskImpact,
  RiskLevel,
  RiskMatrix,
  RiskMatrixConfig,
  RiskStatistics,
  RiskHeatMapCell,
  RiskFilter,
} from '../types/risk';
import { DEFAULT_RISK_MATRIX_CONFIG } from '../types/risk';

// ============================================================================
// RISK CALCULATOR UTILITIES
// ============================================================================

/**
 * Calculate risk score (probability × impact)
 */
export function calculateRiskScore(
  probability: RiskProbability,
  impact: RiskImpact
): number {
  return probability * impact;
}

/**
 * Determine risk level based on score
 */
export function calculateRiskLevel(
  score: number,
  config: RiskMatrixConfig = DEFAULT_RISK_MATRIX_CONFIG
): RiskLevel {
  if (score > config.highThreshold) {
    return 'critical';
  }
  if (score > config.mediumThreshold) {
    return 'high';
  }
  if (score > config.lowThreshold) {
    return 'medium';
  }
  return 'low';
}

/**
 * Update risk with calculated values
 */
export function enrichRisk(
  risk: Partial<Risk>,
  config: RiskMatrixConfig = DEFAULT_RISK_MATRIX_CONFIG
): Risk {
  const probability = risk.probability || 3;
  const impact = risk.impact || 3;
  const score = calculateRiskScore(probability, impact);
  const level = calculateRiskLevel(score, config);

  return {
    id: risk.id || `risk-${Date.now()}`,
    title: risk.title || 'Nouveau risque',
    description: risk.description || '',
    category: risk.category || 'technical',
    probability,
    impact,
    score,
    level,
    status: risk.status || 'identified',
    response: risk.response || 'mitigate',
    owner: risk.owner,
    mitigationPlan: risk.mitigationPlan,
    contingencyPlan: risk.contingencyPlan,
    triggerConditions: risk.triggerConditions,
    identifiedDate: risk.identifiedDate || new Date(),
    lastReviewDate: risk.lastReviewDate,
    targetResolutionDate: risk.targetResolutionDate,
    closedDate: risk.closedDate,
    relatedTasks: risk.relatedTasks || [],
    relatedRisks: risk.relatedRisks || [],
    tags: risk.tags || [],
    attachments: risk.attachments || [],
    notes: risk.notes || [],
    historyLog: risk.historyLog || [],
  } as Risk;
}

/**
 * Calculate risk matrix statistics
 */
export function calculateRiskStatistics(
  risks: Risk[],
  _config: RiskMatrixConfig = DEFAULT_RISK_MATRIX_CONFIG
): RiskStatistics {
  const activeRisks = risks.filter((r) => r.status !== 'closed');

  // Count by level
  const byLevel: Record<RiskLevel, number> = {
    low: 0,
    medium: 0,
    high: 0,
    critical: 0,
  };

  activeRisks.forEach((risk) => {
    byLevel[risk.level]++;
  });

  // Count by category
  const byCategory: Record<string, number> = {
    technical: 0,
    financial: 0,
    organizational: 0,
    external: 0,
    quality: 0,
    schedule: 0,
  };

  activeRisks.forEach((risk) => {
    byCategory[risk.category]++;
  });

  // Count by status
  const byStatus: Record<string, number> = {
    identified: 0,
    analyzing: 0,
    mitigating: 0,
    monitoring: 0,
    closed: 0,
    accepted: 0,
  };

  risks.forEach((risk) => {
    byStatus[risk.status]++;
  });

  // Calculate average score
  const averageScore =
    activeRisks.length > 0
      ? activeRisks.reduce((sum, risk) => sum + risk.score, 0) / activeRisks.length
      : 0;

  // Get top risks (critical and high, sorted by score)
  const topRisks = activeRisks
    .filter((r) => r.level === 'critical' || r.level === 'high')
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  // Get recently identified risks (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentlyIdentified = activeRisks
    .filter((r) => r.identifiedDate >= thirtyDaysAgo)
    .sort((a, b) => b.identifiedDate.getTime() - a.identifiedDate.getTime())
    .slice(0, 5);

  // Get overdue reviews (review date passed)
  const now = new Date();
  const overdueReviews = activeRisks
    .filter((r) => r.targetResolutionDate && r.targetResolutionDate < now)
    .sort((a, b) => {
      const aDate = a.targetResolutionDate?.getTime() || 0;
      const bDate = b.targetResolutionDate?.getTime() || 0;
      return aDate - bDate;
    });

  // Determine trend (simplified - could be more sophisticated)
  let trend: 'improving' | 'stable' | 'worsening' = 'stable';
  const criticalCount = byLevel.critical;
  const highCount = byLevel.high;

  if (criticalCount > 3 || highCount > 5) {
    trend = 'worsening';
  } else if (criticalCount === 0 && highCount < 2) {
    trend = 'improving';
  }

  return {
    total: activeRisks.length,
    byLevel: byLevel as Record<RiskLevel, number>,
    byCategory: byCategory as any,
    byStatus: byStatus as any,
    averageScore,
    topRisks,
    recentlyIdentified,
    overdueReviews,
    trend,
  };
}

/**
 * Generate risk matrix heat map data
 */
export function generateHeatMap(
  risks: Risk[],
  config: RiskMatrixConfig = DEFAULT_RISK_MATRIX_CONFIG
): RiskHeatMapCell[][] {
  const size = config.matrixSize;
  const heatMap: RiskHeatMapCell[][] = [];

  // Initialize heat map grid
  for (let prob = size; prob >= 1; prob--) {
    const row: RiskHeatMapCell[] = [];
    for (let imp = 1; imp <= size; imp++) {
      const score = calculateRiskScore(prob as RiskProbability, imp as RiskImpact);
      const level = calculateRiskLevel(score, config);
      const cellRisks = risks.filter(
        (r) => r.probability === prob && r.impact === imp && r.status !== 'closed'
      );

      row.push({
        probability: prob as RiskProbability,
        impact: imp as RiskImpact,
        score,
        level,
        risks: cellRisks,
        count: cellRisks.length,
      });
    }
    heatMap.push(row);
  }

  return heatMap;
}

/**
 * Filter risks based on criteria
 */
export function filterRisks(risks: Risk[], filter: RiskFilter): Risk[] {
  let filtered = [...risks];

  // Filter by categories
  if (filter.categories && filter.categories.length > 0) {
    filtered = filtered.filter((r) => filter.categories!.includes(r.category));
  }

  // Filter by statuses
  if (filter.statuses && filter.statuses.length > 0) {
    filtered = filtered.filter((r) => filter.statuses!.includes(r.status));
  }

  // Filter by levels
  if (filter.levels && filter.levels.length > 0) {
    filtered = filtered.filter((r) => filter.levels!.includes(r.level));
  }

  // Filter by owners
  if (filter.owners && filter.owners.length > 0) {
    filtered = filtered.filter((r) => r.owner && filter.owners!.includes(r.owner));
  }

  // Filter by tags
  if (filter.tags && filter.tags.length > 0) {
    filtered = filtered.filter((r) =>
      filter.tags!.some((tag) => r.tags.includes(tag))
    );
  }

  // Filter by date range
  if (filter.dateRange) {
    filtered = filtered.filter(
      (r) =>
        r.identifiedDate >= filter.dateRange!.start &&
        r.identifiedDate <= filter.dateRange!.end
    );
  }

  // Filter by search query
  if (filter.searchQuery && filter.searchQuery.trim().length > 0) {
    const query = filter.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (r) =>
        r.title.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query) ||
        r.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  }

  return filtered;
}

/**
 * Sort risks by different criteria
 */
export function sortRisks(
  risks: Risk[],
  sortBy: 'score' | 'date' | 'category' | 'status' = 'score'
): Risk[] {
  const sorted = [...risks];

  switch (sortBy) {
    case 'score':
      return sorted.sort((a, b) => b.score - a.score);

    case 'date':
      return sorted.sort(
        (a, b) => b.identifiedDate.getTime() - a.identifiedDate.getTime()
      );

    case 'category':
      return sorted.sort((a, b) => a.category.localeCompare(b.category));

    case 'status':
      return sorted.sort((a, b) => a.status.localeCompare(b.status));

    default:
      return sorted;
  }
}

/**
 * Get risk color based on level
 */
export function getRiskColor(level: RiskLevel): string {
  switch (level) {
    case 'low':
      return '#10b981'; // Green
    case 'medium':
      return '#f59e0b'; // Orange
    case 'high':
      return '#ef4444'; // Red
    case 'critical':
      return '#dc2626'; // Dark Red
    default:
      return '#6b7280'; // Gray
  }
}

/**
 * Get risk background color (lighter version)
 */
export function getRiskBackgroundColor(level: RiskLevel): string {
  switch (level) {
    case 'low':
      return 'rgba(16, 185, 129, 0.1)';
    case 'medium':
      return 'rgba(245, 158, 11, 0.15)';
    case 'high':
      return 'rgba(239, 68, 68, 0.2)';
    case 'critical':
      return 'rgba(220, 38, 38, 0.25)';
    default:
      return 'rgba(107, 114, 128, 0.1)';
  }
}

/**
 * Get probability label
 */
export function getProbabilityLabel(probability: RiskProbability): string {
  switch (probability) {
    case 1:
      return 'Très faible';
    case 2:
      return 'Faible';
    case 3:
      return 'Moyen';
    case 4:
      return 'Élevé';
    case 5:
      return 'Très élevé';
    default:
      return 'Inconnu';
  }
}

/**
 * Get impact label
 */
export function getImpactLabel(impact: RiskImpact): string {
  switch (impact) {
    case 1:
      return 'Très faible';
    case 2:
      return 'Faible';
    case 3:
      return 'Moyen';
    case 4:
      return 'Élevé';
    case 5:
      return 'Très élevé';
    default:
      return 'Inconnu';
  }
}

/**
 * Get category label
 */
export function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    technical: 'Technique',
    financial: 'Financier',
    organizational: 'Organisationnel',
    external: 'Externe',
    quality: 'Qualité',
    schedule: 'Planning',
  };
  return labels[category] || category;
}

/**
 * Get status label
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    identified: 'Identifié',
    analyzing: 'En analyse',
    mitigating: 'En atténuation',
    monitoring: 'En surveillance',
    closed: 'Clôturé',
    accepted: 'Accepté',
  };
  return labels[status] || status;
}

/**
 * Check if risk needs review
 */
export function needsReview(risk: Risk, daysThreshold: number = 30): boolean {
  if (!risk.lastReviewDate) {
    return true;
  }

  const daysSinceReview =
    (Date.now() - risk.lastReviewDate.getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceReview >= daysThreshold;
}

/**
 * Generate risk matrix from project
 */
export function generateRiskMatrix(
  projectId: string,
  risks: Risk[]
): RiskMatrix {
  const stats = calculateRiskStatistics(risks);

  return {
    id: `risk-matrix-${projectId}`,
    projectId,
    risks,
    totalRisks: stats.total,
    criticalRisks: stats.byLevel.critical,
    highRisks: stats.byLevel.high,
    mediumRisks: stats.byLevel.medium,
    lowRisks: stats.byLevel.low,
    identifiedRisks: stats.byStatus.identified || 0,
    mitigatingRisks: stats.byStatus.mitigating || 0,
    monitoringRisks: stats.byStatus.monitoring || 0,
    closedRisks: stats.byStatus.closed || 0,
    lastUpdated: new Date(),
    createdAt: new Date(),
  };
}
